// Module `access` (API-v3 5): sign-in, sign-up, verification, onboarding,
// team, SSO, account, partners. Owned by WP access after the foundation.
import { z } from "zod";
import {
  NOTIFICATION_KINDS,
  ONBOARDING_STEPS,
  ORG_TYPES,
  RETENTION_POLICIES,
  SSO_CONNECTION_TYPES,
  TOR_FIELD_KINDS,
  type AuthMethod,
  WORK_MODES,
  type OnboardingStep,
  type OrgRole,
} from "../enums";
import { LIMITS } from "../limits";
import { NUMBER_FORMATS } from "../money";
import { email, uuid } from "../schemas/common";
import { country, text, textOrEmpty, textOrNull, type ActorRef, type FileRef, type Id, type MeV3, type ProjectRef, type UserRef } from "./common";
import type { MailStatus } from "./mail";

/** A new password: 12..200 characters (SPEC-v3 D3). The common-password check runs on the server. */
export const newPassword = z
  .string()
  .min(LIMITS.passwordMin, `Use at least ${LIMITS.passwordMin} characters.`)
  .max(LIMITS.passwordMax, `Use ${LIMITS.passwordMax} characters or fewer.`);
/** A password typed to sign in: never length-checked beyond the maximum (old 10-character passwords keep working). */
export const anyPassword = z.string().min(1, "Enter your password.").max(LIMITS.passwordMax);
export const sixDigitCode = z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code.");
export const audience = z.enum(["team", "expert"]);
export type Audience = z.infer<typeof audience>;

// ---------- 5.1 sign-in, sign-up, verification ----------

export type AuthStatus = "signed_in" | "verify_email" | "mfa_required" | "mfa_enrol";
export type AuthResult = { status: AuthStatus; next: string; email: string; me: MeV3 | null };

export const loginBody = z.object({ email, password: anyPassword, remember: z.boolean().default(true), audience: audience.default("team") });
export type LoginBody = z.infer<typeof loginBody>;

export const ssoDiscoverBody = z.object({ email });
/** GET /api/auth/sso/providers (v3 buttons; GET /api/auth-methods replaces it in the web's step 9). */
export type SsoProviders = { microsoft: boolean; google: boolean };
export type SsoDiscoverResult = { redirectUrl: string | null };
/**
 * GET /api/auth-methods: the sign-in methods this deployment offers (listed in
 * AUTH_METHODS and configured). `sso` = company sign-in is on (the email-domain
 * discovery page). A method not listed here is also refused by the server.
 */
export type AuthMethodsResult = { methods: AuthMethod[]; sso: boolean };

export const signupTeamBody = z
  .object({
    name: text(LIMITS.userName, "Name"),
    email,
    password: newPassword,
    intent: z.enum(["setup_org", "invited", "partner"]),
    /** Setting up: the organisation made right after the email code (required for setup_org). */
    orgName: text(LIMITS.orgName, "Organisation name").optional(),
    termsAccepted: z.literal(true, { errorMap: () => ({ message: "Accept the terms to continue." }) }),
    marketingOptIn: z.boolean().default(false),
    /** The Cloudflare Turnstile answer, when the deployment has the bot check (GET /api/meta turnstileSiteKey). */
    turnstileToken: z.string().max(4096).optional(),
  })
  .superRefine((b, ctx) => {
    if (b.intent === "setup_org" && !b.orgName) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["orgName"], message: "Enter your organisation's name." });
  });
export type SignupTeamBody = z.infer<typeof signupTeamBody>;

export const signupExpertBody = z.object({
  name: text(LIMITS.userName, "Name"),
  email,
  password: newPassword.nullable(),
  country,
  invitationCode: z.string().trim().max(20).nullable(),
  cvFileId: uuid.nullable(),
  termsAccepted: z.literal(true, { errorMap: () => ({ message: "Accept the terms to continue." }) }),
  shareProfileConsent: z.boolean(),
  /** The Cloudflare Turnstile answer, as on the team sign-up. */
  turnstileToken: z.string().max(4096).optional(),
});
export type SignupExpertBody = z.infer<typeof signupExpertBody>;

export type ExpertInvitationInfo = {
  orgName: string;
  projectName: string | null;
  category: "ke" | "nke";
  level: "senior" | "junior" | null;
};

export const verifyEmailBody = z.object({ code: sixDigitCode });
export const verifyEmailChangeBody = z.object({ email });
export const magicLinkBody = z.object({ email, audience: audience.default("team"), remember: z.boolean().optional() });
export const magicLinkConsumeBody = z.object({ token: z.string().trim().min(20).max(200) });
export const forgotPasswordBody = z.object({ email });
export const resetPasswordBody = z.object({ token: z.string().min(20).max(200), password: newPassword, signOutOthers: z.boolean().default(false) });
export const twoFactorVerifyBody = z
  .object({
    /** One field for the 6-digit app code OR a backup code; the server tells them apart (AUTH-v4 11). */
    code: z.string().trim().min(6).max(24).optional(),
    /** Older browser tabs (kept one release): a backup code in its own field. */
    backupCode: z.string().trim().min(8).max(20).optional(),
    trustDevice: z.boolean().default(false),
  })
  .refine((v) => Boolean(v.code) !== Boolean(v.backupCode), "Enter the code from your app or one backup code.");
export type TwoFactorVerifyBody = z.infer<typeof twoFactorVerifyBody>;
export type TotpEnrolStart = { otpauthUri: string; secretBase32: string; /** WP access addition: the QR code as an SVG data URL. */ qrDataUrl?: string };
export const twoFactorConfirmBody = z.object({ code: sixDigitCode });
export type TotpEnrolConfirm = { backupCodes: string[]; auth: AuthResult };
export const currentCodeBody = z.object({ currentCode: sixDigitCode });

export type JoinInfo = {
  orgName: string;
  email: string;
  role: OrgRole | "partner";
  projectName: string | null;
  partnerName: string | null;
  accountExists: boolean;
};
export const joinAcceptBody = z.object({
  name: text(LIMITS.userName, "Name").optional(),
  password: newPassword.optional(),
  termsAccepted: z.boolean().optional(),
});

// ---------- 5.2 onboarding ----------

export const onboardingStep = z.enum(ONBOARDING_STEPS);
export const orgProfileBody = z.object({
  name: text(LIMITS.orgName, "Organisation name"),
  country: country.nullable(),
  orgType: z.enum(ORG_TYPES).nullable(),
  workMode: z.enum(WORK_MODES).nullable(),
  logoFileId: uuid.nullable(),
});
export type OrgProfileBody = z.infer<typeof orgProfileBody>;
export const orgProfilePatch = orgProfileBody.partial().extend({ defaultTorBoilerplate: textOrEmpty(LIMITS.boilerplate).optional() });
/** AI import settings (docs/v3/AI-IMPORT.md 8). `available`: this server has AI at all. */
export type OrgAiSettings = { available: boolean; enabled: boolean; capCents: number; spentCents: number; assistantEnabled: boolean };
/** M2: the owner's two-step setting: codes by email allowed, or an authenticator app required. */
export const orgTwoStepPatch = z.object({ requireAuthenticator: z.boolean() });
export type OrgTwoStepSettings = { requireAuthenticator: boolean; canChange: boolean; membersUsingEmail: number };
export const orgAiPatch = z.object({ enabled: z.boolean().optional(), capCents: z.number().int().min(0).max(10_000_000).optional(), assistantEnabled: z.boolean().optional() });
export type OrgProfile = OrgProfileBody & {
  id: Id;
  logoUrl: string | null;
  allowDomainJoin: boolean;
  allowedDomain: string | null;
  defaultTorBoilerplate: string;
};
export type Onboarding = {
  steps: Record<OnboardingStep, "todo" | "done" | "skipped">;
  counts: { invited: number; contacts: number; experts: number };
  onboardedAt: string | null;
};
export const onboardingPatch = z.object({ step: onboardingStep, state: z.enum(["done", "skipped"]) });

/** The "Get started" checklist's items, in the order shown; the last two are optional. */
export const GET_STARTED_ITEMS = ["organisation", "project", "team", "houseStyle", "experts"] as const;
export type GetStartedItemKey = (typeof GET_STARTED_ITEMS)[number];
export const GET_STARTED_OPTIONAL: readonly GetStartedItemKey[] = ["houseStyle", "experts"];
/**
 * GET /api/org/get-started: the checklist for an owner or admin of an org that
 * is still being set up, or `checklist: null` when there is nothing to show
 * (other roles, the demo, hidden, or every required item done).
 */
export type GetStarted = {
  checklist: { items: { key: GetStartedItemKey; done: boolean; optional: boolean }[] } | null;
};

export const templateField = z.object({
  key: z.string().trim().min(1).max(60),
  label: text(200, "Label"),
  kind: z.enum(TOR_FIELD_KINDS),
  fromRecord: z.string().max(200).nullable(),
});
export type TemplateField = z.infer<typeof templateField>;
export const templateSection = z.object({
  key: z.string().trim().min(1).max(60),
  title: text(LIMITS.sectionTitle, "Section title"),
  fields: z.array(templateField).max(40),
});
export type TemplateSection = z.infer<typeof templateSection>;

export const houseStyleBody = z.object({
  templateSource: z.enum(["standard", "uploaded"]),
  sections: z.array(templateSection).max(LIMITS.sectionsMax),
  templateFileId: uuid.nullable(),
  clauses: textOrEmpty(LIMITS.boilerplate),
  bannerFileId: uuid.nullable(),
  showEuFunding: z.boolean(),
  retentionPolicy: z.enum(RETENTION_POLICIES),
  dpoEmail: email.nullable(),
});
export type HouseStyleBody = z.infer<typeof houseStyleBody>;
export type HouseStyle = HouseStyleBody & { bannerUrl: string | null; usedOnProjects: number };

export const libraryAnalyseBody = z.object({ kind: z.enum(["contacts", "cvs"]), fileIds: z.array(uuid).min(1).max(LIMITS.cvBatchMax) });
export type LibraryBatch = {
  id: Id;
  kind: "contacts" | "cvs";
  files: FileRef[];
  status: "analysing" | "ready" | "committed" | "failed";
  use: boolean;
  summary: string;
  error: string | null;
};
export const libraryPatch = z.object({ use: z.boolean() });
export type LibraryCommitResult = { contactsCreated: number; contactsMatched: number; orgsCreated: number; expertsCreated: number };

// ---------- 5.3 team, invitations, roles, join requests, SSO ----------

export type Member = { user: UserRef; role: OrgRole; joinedAt: string; isYou: boolean };
export type MemberDetail = Member & { twoStep: boolean; projects: ProjectRef[] };
export type InvitationV3 = {
  id: Id;
  email: string;
  role: OrgRole | "expert";
  invitedBy: ActorRef;
  createdAt: string;
  expiresAt: string;
  project: ProjectRef | null;
};
export const memberRolePatch = z.object({ role: z.enum(["admin", "member", "viewer", "expert"]) });
export const inviteBody = z.object({ email, role: z.enum(["admin", "member", "viewer", "expert"]), projectId: uuid.optional() });
export const bulkInviteBody = z.object({
  invitations: z.array(z.object({ email, role: z.enum(["admin", "member", "viewer"]) })).min(1).max(LIMITS.bulkInviteMax),
  allowDomainJoin: z.boolean(),
});
export type BulkInviteResult = {
  created: InvitationV3[];
  skipped: { email: string; reason: "already_member" | "already_invited" | "invalid" }[];
};
export type JoinRequest = { id: Id; user: UserRef; createdAt: string };
export const approveJoinBody = z.object({ role: z.enum(["member", "viewer"]) });

export const ssoConnectionBody = z.object({
  type: z.enum(SSO_CONNECTION_TYPES),
  displayName: text(120, "Name"),
  domains: z.array(z.string().trim().toLowerCase().regex(/^[a-z0-9.-]+\.[a-z]{2,}$/, "Enter a domain like example.org.")).max(20),
  issuer: z.string().trim().url().nullable(),
  clientId: textOrNull(300),
  /**
   * The OIDC client secret itself: stored encrypted, never shown again (write-only).
   * Left out on a change = keep the stored one; null = remove it.
   */
  clientSecret: z.string().trim().min(1, "Enter the client secret.").max(500).nullable().optional(),
  samlEntryPoint: z.string().trim().url().nullable(),
  samlCert: textOrNull(20_000),
  /**
   * SAML: the identity provider's entity ID (the Issuer of its responses).
   * Optional: when it is left empty, Convenor learns it from the first
   * response whose signature the certificate above proves.
   */
  samlIdpEntityId: textOrNull(500).optional(),
  autoProvisionRole: z.enum(["member", "viewer"]).nullable(),
  enabled: z.boolean(),
});
export type SsoConnectionBody = z.infer<typeof ssoConnectionBody>;
export type SsoConnection = Omit<SsoConnectionBody, "clientSecret"> & {
  id: Id;
  /** Register this address at the identity provider: the OIDC redirect URI, or the SAML reply (ACS) URL. */
  redirectUrl: string;
  hasSecret: boolean;
  /** The domains proven by their DNS TXT record: only these route sign-ins to this connection. */
  verifiedDomains: string[];
  /** The TXT record to add to each domain. */
  domainRecord: string;
  createdAt: string;
};
export type SsoSettings = { connections: SsoConnection[]; spMetadataUrl: string; callbackUrl: string };

// ---------- 5.4 account ----------

export const mePatch = z
  .object({
    name: text(LIMITS.userName, "Name").optional(),
    notificationPrefs: z.record(z.enum(NOTIFICATION_KINDS), z.object({ inApp: z.boolean(), email: z.boolean() })).optional(),
    marketingOptIn: z.boolean().optional(),
    /** v1 switch, still read by the v1/v2 ToR emails: false turns every email notification off. */
    notifyEmail: z.boolean().optional(),
    /** R2-008: "eu" (10.000,00) or "us" (10,000.00), for what the web app shows this person. */
    numberFormat: z.enum(NUMBER_FORMATS).optional(),
  })
  .strict();
export const changeEmailBody = z.object({ email, currentPassword: anyPassword.optional() });
export const confirmEmailBody = z.object({ code: sixDigitCode });
export const changePasswordBody = z.object({ currentPassword: anyPassword, newPassword, signOutOthers: z.boolean().default(false) });
export const switchOrgBody = z.object({ orgId: uuid });

// ---------- 5.5 partners ----------

export const partnerGrant = z.object({
  resultIds: z.array(uuid).max(100),
  activityIds: z.array(uuid).max(500),
  budgetLineIds: z.array(uuid).max(100),
  uploadEvidence: z.boolean(),
  logMeetings: z.boolean(),
  submitTimesheets: z.boolean(),
  submitExpenses: z.boolean(),
  seeBudget: z.boolean(),
  seeOtherStakeholders: z.boolean(),
});
export type PartnerGrant = z.infer<typeof partnerGrant>;
export const EMPTY_PARTNER_GRANT: PartnerGrant = {
  resultIds: [],
  activityIds: [],
  budgetLineIds: [],
  uploadEvidence: false,
  logMeetings: false,
  submitTimesheets: false,
  submitExpenses: false,
  seeBudget: false,
  seeOtherStakeholders: false,
};
export const partnerBody = z.object({
  name: text(200, "Partner name"),
  partnerType: textOrEmpty(80),
  stakeholderOrgId: uuid.nullable(),
  grant: partnerGrant,
});
export type PartnerBody = z.infer<typeof partnerBody>;
export type PartnerRow = { id: Id; name: string; roleLabel: string; sees: string; people: number };
export type PartnerPerson = {
  id: Id;
  name: string | null;
  email: string;
  roleLabel: string;
  status: "invited" | "active";
  /** The latest invitation email: queued, sent, trying again or failed (EM3); null once accepted or never sent. */
  mail: MailStatus | null;
};
/** POST .../partners/:id/people: the person, plus a warning when the address is a team member's (GL5). */
export type PartnerPersonInvited = PartnerPerson & { warning: string | null };
export type PartnerDetail = PartnerBody & {
  id: Id;
  people: PartnerPerson[];
  options: {
    results: { id: Id; code: string; title: string; shortName: string | null }[];
    activities: { id: Id; code: string; title: string }[];
    budgetLines: { id: Id; number: number; name: string; heading?: string | null; code?: string | null }[];
  };
};
/** confirmTeamMember (GL5): the address is someone of the own team, and the lead confirmed the invitation anyway. */
export const partnerPersonBody = z.object({ email, roleLabel: textOrEmpty(120), confirmTeamMember: z.boolean().optional() });

// ---------- additions of WP access (optional, additive) ----------

/** The standard ToR template ("Heading block, Task description, Outputs, Expert profile"). */
export const STANDARD_TOR_TEMPLATE: TemplateSection[] = [
  {
    key: "heading",
    title: "Heading block",
    fields: [
      { key: "title", label: "Title", kind: "short_text", fromRecord: "activity.title" },
      { key: "reference", label: "Reference", kind: "short_text", fromRecord: "tor.reference" },
      { key: "dates", label: "Dates", kind: "short_text", fromRecord: "activity.dates" },
      { key: "location", label: "Location", kind: "short_text", fromRecord: "activity.location" },
    ],
  },
  {
    key: "task",
    title: "Task description",
    fields: [
      { key: "background", label: "Background", kind: "long_text", fromRecord: null },
      { key: "objective", label: "Objective", kind: "long_text", fromRecord: null },
      { key: "tasks", label: "Tasks", kind: "list", fromRecord: null },
    ],
  },
  {
    key: "outputs",
    title: "Outputs",
    fields: [{ key: "outputs", label: "Outputs", kind: "list", fromRecord: null }],
  },
  {
    key: "experts",
    title: "Expert profile",
    fields: [
      { key: "experts", label: "Experts", kind: "experts", fromRecord: "tor.experts" },
      { key: "qualifications", label: "Qualifications", kind: "long_text", fromRecord: null },
    ],
  },
];

/** Free mail providers: never used for "anyone with an @domain email may ask to join" (SPEC-v3 6.10). */
export const PUBLIC_MAIL_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "yahoo.com",
  "yahoo.fr",
  "icloud.com",
  "me.com",
  "aol.com",
  "gmx.de",
  "gmx.net",
  "web.de",
  "proton.me",
  "protonmail.com",
  "mail.com",
  "yandex.com",
  "zoho.com",
  "orange.fr",
  "free.fr",
  "libero.it",
] as const;

/** The domain of an email address, lower-cased ("" when there is none). */
export const emailDomain = (address: string): string => (address.split("@")[1] ?? "").trim().toLowerCase();

/** Attach the CV chosen on the expert sign-up page (uploaded right after the account exists). */
export const signupExpertCvBody = z.object({ fileId: uuid });

/** The signed-in person's own pending request to join an organisation (Org Profile shows it). */
export type MyJoinRequest = { id: Id; orgName: string; createdAt: string } | null;

/** GET /org/members also answers `items` in the v1 shape until the integration step removes it. */
export type MembersResponse = { members: Member[]; canManage: boolean };
export type InvitationsResponse = { invitations: InvitationV3[] };
export type JoinRequestsResponse = { requests: JoinRequest[] };
export type PartnersResponse = { partners: PartnerRow[]; canManage: boolean };
export type OrgCreated = { org: OrgProfile; me: MeV3 };

// ---------- API keys (docs/v3/BULK-IMPORT.md 3) ----------

/** POST /api/account/api-keys: a key for the bulk import, valid 1 to 30 days. */
export const apiKeyCreateBody = z.object({ name: text(80, "Name"), days: z.coerce.number().int().min(1).max(30).default(7) });
export type ApiKeyCreateBody = z.infer<typeof apiKeyCreateBody>;
export type ApiKeyDto = { id: Id; name: string; prefix: string; scope: "import"; expiresAt: string; lastUsedAt: string | null; revokedAt: string | null; createdAt: string };
/** The key itself is in `token`, shown this once. */
export type ApiKeyCreated = { key: ApiKeyDto; token: string };
